import React, { useEffect, useState, useContext } from "react";
import Label from "components/lib/Shared/Label";
import Button from "components/lib/Shared/Button";
import { CHANGE_TYPES, OFFICIALS } from "config/companyChangeOfficialsOptions";
import { Constants } from "config/constants";
import { Roles_Permissions_Constants } from "config/roles-permissions";
import { useSelector } from "react-redux";
import { checkIfDirectorsMeetThresholdAsPerTheGovernanceSummary } from "utils/companyGovernanceSummaryReferral";
import TransactionEditContext from "context/TransactionEditContext";

const SelectChangeForm = ({
  formData,
  setFormData,
  handleSubmit,
  setWhichFormToBeRendered,
  formStepErrors,
  setFormStepErrors,
  setSimilarTransactionIsUnderway,
  showProceedButton,
  setShowProceedButton,
  setShowCompanyOfficialsForm,
}) => {
  const { company, dashboardPermissions, dashboardRoles, designation } =
    useSelector((state) => state.companyDashboardSlice);

  const { isInTransactionEditContext } = useContext(TransactionEditContext);

  const companyOfficials = useSelector(
    (state) => state.companyOfficialsSlice.companyOfficials.items
  );

  const { initiatedTransactions } = useSelector(
    (state) => state.initiatedTransactionsSlice
  );

  const hasEditOtherDataPermission = dashboardPermissions.some(
    (permission) =>
      permission.name ===
      Roles_Permissions_Constants.DASHBOARD_PERMISSIONS.EDIT_OTHER_DATA
  );

  const filteredOfficials = hasEditOtherDataPermission
    ? OFFICIALS
    : OFFICIALS.filter((official) => official.title === designation);

  const filteredChangeTypes = hasEditOtherDataPermission
    ? CHANGE_TYPES
    : CHANGE_TYPES.filter((type) => type !== "Appointment");

  const filteredTransactions =
    initiatedTransactions?.items.filter((transaction) => {
      return (
        transaction.changeType &&
        Object.values(Constants.MAINTAIN_COMPANY_DRAFT_STATUS).includes(
          transaction.status
        )
      );
    }) || [];

  const [officialDesignation, setOfficialDesignation] = useState("");

  useEffect(() => {
    if (filteredOfficials.length === 1 && !isInTransactionEditContext) {
      const singleOfficial = filteredOfficials[0];
      setOfficialDesignation(singleOfficial.id);
      setFormData((prevState) => ({
        ...prevState,
        selectedDesignation: singleOfficial.title,
        idType: "",
        idTypeOptions: [
          "directorShareholders",
          "shareholders",
          "jointShareholders",
          "beneficialOwners",
        ].includes(singleOfficial.id)
          ? ["Change of Particulars"]
          : filteredChangeTypes,
      }));
    }
  }, []);

  let selectedDesignation = "";
  if (officialDesignation) {
    const { title } = filteredOfficials.find(
      (official) => official.id === officialDesignation && official.title
    );
    selectedDesignation = title;
  }

  const getTotalDirectorsPresentFromCompanyOfficials = (companyOfficials) => {
    const totalDirectors = companyOfficials.filter(
      (official) =>
        official?.designation === "Director" ||
        official?.designation === "Director Shareholder"
    ).length;
    return totalDirectors;
  };

  const totalNumberOfDirectorsPresent =
    getTotalDirectorsPresentFromCompanyOfficials(companyOfficials);
  const directorsMeetThreshold =
    checkIfDirectorsMeetThresholdAsPerTheGovernanceSummary(
      totalNumberOfDirectorsPresent,
      company
    );

  const handleDesignationChange = (event) => {
    setOfficialDesignation(event.target.value);
    const selectedDesignation = event.target.value;
    const selectedOfficialId = filteredOfficials.find(
      (official) => official.id === selectedDesignation
    );
    setWhichFormToBeRendered("");
    setFormStepErrors({ ...formStepErrors, selectedOfficial: "" });
    const restrictedDesignations = [
      "directorShareholders",
      "shareholders",
      "jointShareholders",
      "beneficialOwners",
    ];
    const newIdTypeOptions = restrictedDesignations.includes(
      selectedDesignation
    )
      ? ["Change of Particulars"]
      : filteredChangeTypes;

    setFormData((prevState) => ({
      ...prevState,
      selectedDesignation: selectedOfficialId.title,
      idType: "",
      idTypeOptions: newIdTypeOptions,
    }));
  };

  const handleIdTypeChange = (event) => {
    setFormData((prevState) => ({
      ...prevState,
      idType: event.target.value,
    }));
    setFormStepErrors({ ...formStepErrors, typeOfChange: "" });
    const idTypeIsInDraft = filteredTransactions.some(
      (trx) =>
        trx.changeType.toLowerCase() === event.target.value.toLowerCase() &&
        selectedDesignation === trx.applicationType
    );
    setSimilarTransactionIsUnderway(idTypeIsInDraft);
    setWhichFormToBeRendered("");
    setShowProceedButton(true);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setShowProceedButton(false);
    setShowCompanyOfficialsForm(true);
    handleSubmit(e);
  };
  return (
    <form className="mt-4" onSubmit={handleFormSubmit}>
      <div className="px-4 py-6 mt-2 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md">
        <div className="grid grid-cols-6 gap-6">
          <div className="col-span-6 sm:col-span-3">
            <Label htmlFor="designation">Officials ID</Label>
            <select
              name="designation"
              className="block w-full px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              id="designation"
              onChange={handleDesignationChange}
              value={
                formData.designation
                  ? formData.designation
                  : officialDesignation
              }
              disabled={
                isInTransactionEditContext ||
                (filteredOfficials.length === 1 && !isInTransactionEditContext)
              }
            >
              <option value="">
                {formData.designation
                  ? formData.designation
                  : "Select designation"}
              </option>
              {filteredOfficials.map((official) => (
                <option key={official.id} value={official.id}>
                  {official.title}
                </option>
              ))}
            </select>
            {formStepErrors.selectedOfficial !== "" && (
              <small className="text-red-500 mt-2 py-4 px-1 font-semibold">
                {formStepErrors.selectedOfficial}
              </small>
            )}
          </div>
          <div className="col-span-6 sm:col-span-3">
            <Label htmlFor="idType">Type Of Change</Label>
            <select
              name="idType"
              className="block w-full px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              id="idType"
              onChange={handleIdTypeChange}
              value={formData.idType} // Default to first option if no value is set
              disabled={isInTransactionEditContext}
            >
              <option value="">
                {formData.idType ? formData.idType : "Select type of change"}
              </option>
              {formData.idTypeOptions.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {formStepErrors.typeOfChange !== "" && (
              <small className="text-red-500 mt-2 py-4 px-1 font-semibold">
                {formStepErrors.typeOfChange}
              </small>
            )}
            {!isInTransactionEditContext && showProceedButton && (
              <div className="flex justify-end mt-4">
                <Button disabled={isInTransactionEditContext} type="submit">
                  {" "}
                  Proceed{" "}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </form>
  );
};

export default SelectChangeForm;
